import { Injectable } from '@angular/core';
import { Event as RouterEvent, NavigationEnd, Router } from '@angular/router';

import { Observable, Subject } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

export interface PatientInfo {
  firstName: string;
  lastName: string;
  caseId: string;
  tabIndex?: number;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationHistoryService {
  private historyStack: string[] = [];
  private currentPosition: number = -1;

  private patientInfoMap = new Map<string, PatientInfo>();
  private listNames = new Map<string, string>();

  private tabNames = new Map<number, string>([
    [0, 'Übersicht'],
    [1, 'Curve'],
    [2, 'Akte'],
    [3, 'Workflow'],
  ]);

  private navigationSubject = new Subject<void>();

  constructor(private router: Router) {
    this.router.events
      .pipe(
        filter((event: RouterEvent): event is NavigationEnd => event instanceof NavigationEnd),
        tap((event: NavigationEnd) => {
          if (event.urlAfterRedirects === '/start' || event.urlAfterRedirects === '/') {
            if (
              this.currentPosition === -1 ||
              (this.currentPosition >= 0 &&
                this.historyStack[this.currentPosition] !== '/start' &&
                this.historyStack[this.currentPosition] !== '/')
            ) {
              this.updateHistory(event.urlAfterRedirects);
            }
          } else {
            this.updateHistory(event.urlAfterRedirects);
          }

          this.updatePatientTabFromUrl(event.urlAfterRedirects);
          this.navigationSubject.next();
        }),
      )
      .subscribe();
  }

  public getNavigationEvents(): Observable<void> {
    return this.navigationSubject.asObservable();
  }

  public async onClickOnBack(): Promise<void> {
    if (this.canNavigateBack()) {
      this.currentPosition--;
      const url = this.historyStack[this.currentPosition];

      if (url === '/' || url === '/start') {
        await this.router.navigateByUrl(url, { skipLocationChange: false });
      } else {
        await this.router.navigateByUrl(url);
      }
    }
  }

  public async onClickOnForward(): Promise<void> {
    if (this.canNavigateForward()) {
      this.currentPosition++;
      const url = this.historyStack[this.currentPosition];

      if (url === '/' || url === '/start') {
        await this.router.navigateByUrl(url, { skipLocationChange: false });
      } else {
        await this.router.navigateByUrl(url);
      }
    }
  }

  public canNavigateBack(): boolean {
    return this.currentPosition > 0;
  }

  public canNavigateForward(): boolean {
    return this.currentPosition < this.historyStack.length - 1;
  }

  public setPatientInfo(caseId: string, patientInfo: PatientInfo): void {
    const existingInfo = this.patientInfoMap.get(caseId);
    if (existingInfo?.tabIndex !== undefined && patientInfo.tabIndex === undefined) {
      patientInfo.tabIndex = existingInfo.tabIndex;
    }

    if (patientInfo.tabIndex === undefined) {
      patientInfo.tabIndex = 0;
    }

    this.updatePatientTabFromCurrentUrl(caseId, patientInfo);

    this.patientInfoMap.set(caseId, patientInfo);
    this.navigationSubject.next();
  }

  public getPatientInfo(caseId: string): PatientInfo | undefined {
    return this.patientInfoMap.get(caseId);
  }

  public hasPatientInfo(caseId: string): boolean {
    return this.patientInfoMap.has(caseId);
  }

  public getFormattedPatientName(caseId: string): string {
    const info = this.patientInfoMap.get(caseId);
    if (!info) {
      return '';
    }

    // const tabName = info.tabIndex !== undefined
    //   ? this.tabNames.get(info.tabIndex) || `${info.tabIndex}`
    //   : '';

    // const tabDisplay = tabName ? `: ${tabName}` : '';
    // return `${info.firstName[0]}, ${info.lastName}${tabDisplay}`;
    return `${info?.lastName}, ${info?.firstName}`;
  }

  public setListName(listId: string, name: string): void {
    this.listNames.set(listId, name);
    this.listNames.set(decodeURIComponent(listId), name);
    this.listNames.set(encodeURIComponent(listId), name);
  }

  public getListName(listId: string): string | undefined {
    return (
      this.listNames.get(listId) ||
      this.listNames.get(decodeURIComponent(listId)) ||
      this.listNames.get(encodeURIComponent(listId))
    );
  }

  private updateHistory(url: string): void {
    const isRootOrStart = url === '/' || url === '/start';

    if (isRootOrStart && this.historyStack.some(path => path === '/' || path === '/start')) {
      const startIndex = this.historyStack.findIndex(path => path === '/' || path === '/start');
      if (startIndex !== -1) {
        if (this.currentPosition !== startIndex) {
          this.currentPosition = startIndex;
        }
        return;
      }
    }

    if (this.currentPosition === -1 || this.historyStack[this.currentPosition] !== url) {
      this.historyStack = this.historyStack.slice(0, this.currentPosition + 1);
      this.historyStack.push(url);
      this.currentPosition = this.historyStack.length - 1;
    }
  }

  private updatePatientTabFromUrl(url: string): void {
    const patientDetailsMatch = url.match(/patient-details\/(\d+)(?:\/(\d+))?/);
    if (patientDetailsMatch) {
      const caseId = patientDetailsMatch[1];
      const tabIndex = patientDetailsMatch[2] ? parseInt(patientDetailsMatch[2], 10) : 0;

      if (this.patientInfoMap.has(caseId)) {
        const info = this.patientInfoMap.get(caseId)!;
        this.patientInfoMap.set(caseId, {
          ...info,
          tabIndex: tabIndex,
        });
      }
    }
  }

  private updatePatientTabFromCurrentUrl(caseId: string, patientInfo: PatientInfo): void {
    const currentUrl = this.router.url;
    const patientDetailsMatch = currentUrl.match(/patient-details\/(\d+)(?:\/(\d+))?/);

    if (patientDetailsMatch && patientDetailsMatch[1] === caseId) {
      const tabIndex = patientDetailsMatch[2] ? parseInt(patientDetailsMatch[2], 10) : 0;
      patientInfo.tabIndex = tabIndex;
    }
  }
}
